import React, { useState } from 'react';

import { formatRelativeTime } from '../../utils/dateUtils';
import { auditLogs } from '../../utils/stubData';

function AuditLog() {
  const [filters, setFilters] = useState({
    search: '',
    entityType: '',
    action: '',
    dateRange: 'all',
  });

  const handleFilterChange = e => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const filteredLogs = auditLogs.filter(log => {
    const searchMatch =
      !filters.search ||
      log.entityType.toLowerCase().includes(filters.search.toLowerCase()) ||
      log.action.toLowerCase().includes(filters.search.toLowerCase());

    const entityMatch = !filters.entityType || log.entityType === filters.entityType;
    const actionMatch = !filters.action || log.action === filters.action;

    return searchMatch && entityMatch && actionMatch;
  });

  const getActionIcon = action => {
    switch (action) {
      case 'create':
        return '+';
      case 'update':
        return '✎';
      case 'delete':
        return '✕';
      default:
        return '•';
    }
  };

  const getActionColor = action => {
    switch (action) {
      case 'create':
        return 'bg-green-600';
      case 'update':
        return 'bg-blue-600';
      case 'delete':
        return 'bg-red-600';
      default:
        return 'bg-gray-600';
    }
  };

  const formatChanges = (oldValues, newValues) => {
    if (!oldValues && newValues) {
      return (
        <div className="text-sm">
          <span className="text-hdlcc-textMuted">Created with: </span>
          {Object.entries(newValues).map(([key, value], index) => (
            <span key={key}>
              {index > 0 && ', '}
              <span className="text-hdlcc-textMuted">{key}:</span>{' '}
              <span className="text-hdlcc-accentGreen">{JSON.stringify(value)}</span>
            </span>
          ))}
        </div>
      );
    }

    if (oldValues && newValues) {
      return (
        <div className="text-sm space-y-1">
          {Object.entries(newValues).map(([key, newValue]) => {
            const oldValue = oldValues[key];
            if (oldValue !== undefined) {
              return (
                <div key={key}>
                  <span className="text-hdlcc-textMuted">{key}:</span>{' '}
                  <span className="text-hdlcc-accentRed line-through">
                    {JSON.stringify(oldValue)}
                  </span>
                  {' → '}
                  <span className="text-hdlcc-accentGreen">{JSON.stringify(newValue)}</span>
                </div>
              );
            }
            return null;
          })}
        </div>
      );
    }

    return <span className="text-hdlcc-textMuted">No changes recorded</span>;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold text-hdlcc-textDark uppercase">Audit Log</h2>
          <p className="text-hdlcc-textMuted">Track all system changes and actions</p>
        </div>
        <button className="bg-hdlcc-accentRed text-white px-4 py-2 rounded hover:bg-red-600 transition-colors">
          Export Audit Log
        </button>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
          <h4 className="text-hdlcc-textMuted text-sm">Total Actions</h4>
          <p className="text-2xl font-bold mt-2 text-hdlcc-textDark">{auditLogs.length}</p>
          <p className="text-sm text-hdlcc-textMuted mt-1">All time</p>
        </div>
        <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
          <h4 className="text-hdlcc-textMuted text-sm">Created</h4>
          <p className="text-2xl font-bold mt-2 text-hdlcc-accentGreen">
            {auditLogs.filter(l => l.action === 'create').length}
          </p>
          <p className="text-sm text-hdlcc-textMuted mt-1">New records</p>
        </div>
        <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
          <h4 className="text-hdlcc-textMuted text-sm">Updated</h4>
          <p className="text-2xl font-bold mt-2 text-blue-600">
            {auditLogs.filter(l => l.action === 'update').length}
          </p>
          <p className="text-sm text-hdlcc-textMuted mt-1">Modifications</p>
        </div>
        <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
          <h4 className="text-hdlcc-textMuted text-sm">Deleted</h4>
          <p className="text-2xl font-bold mt-2 text-hdlcc-accentRed">
            {auditLogs.filter(l => l.action === 'delete').length}
          </p>
          <p className="text-sm text-hdlcc-textMuted mt-1">Removed records</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card mb-6 border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label htmlFor="audit-search" className="block text-sm text-hdlcc-textMuted mb-1">
              Search
            </label>
            <input
              id="audit-search"
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Entity type or action..."
              className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
            />
          </div>

          <div>
            <label htmlFor="audit-entity-type" className="block text-sm text-hdlcc-textMuted mb-1">
              Entity Type
            </label>
            <select
              id="audit-entity-type"
              name="entityType"
              value={filters.entityType}
              onChange={handleFilterChange}
              className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
            >
              <option value="">All Types</option>
              <option value="member">Member</option>
              <option value="payment">Payment</option>
              <option value="membershipRequest">Membership Request</option>
              <option value="membershipType">Membership Type</option>
              <option value="user">User</option>
            </select>
          </div>

          <div>
            <label htmlFor="audit-action" className="block text-sm text-hdlcc-textMuted mb-1">
              Action
            </label>
            <select
              id="audit-action"
              name="action"
              value={filters.action}
              onChange={handleFilterChange}
              className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
            >
              <option value="">All Actions</option>
              <option value="create">Create</option>
              <option value="update">Update</option>
              <option value="delete">Delete</option>
            </select>
          </div>

          <div>
            <label htmlFor="audit-date-range" className="block text-sm text-hdlcc-textMuted mb-1">
              Date Range
            </label>
            <select
              id="audit-date-range"
              name="dateRange"
              value={filters.dateRange}
              onChange={handleFilterChange}
              className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>
        </div>
      </div>

      {/* Audit Log Entries */}
      <div className="space-y-3">
        {filteredLogs.map(log => (
          <div
            key={log.id}
            className="bg-hdlcc-bgWhite p-4 rounded shadow-card hover:shadow-lg transition-all border border-gray-200"
          >
            <div className="flex items-start space-x-4">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-white flex-shrink-0 ${getActionColor(log.action)}`}
              >
                {getActionIcon(log.action)}
              </div>

              <div className="flex-1">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-semibold capitalize text-hdlcc-textDark">
                      {log.action} {log.entityType}
                    </h4>
                    <p className="text-sm text-hdlcc-textMuted">
                      ID: {log.entityId} • {formatRelativeTime(log.createdAt)}
                    </p>
                  </div>
                  <div className="text-right text-sm">
                    <p className="text-hdlcc-textMuted">User ID: {log.userId}</p>
                    <p className="text-gray-500 text-xs">{log.ipAddress}</p>
                  </div>
                </div>

                {(log.oldValues || log.newValues) && (
                  <div className="mt-3 p-3 bg-gray-50 rounded border border-gray-200">
                    <p className="text-xs font-semibold text-hdlcc-textMuted mb-2">Changes:</p>
                    {formatChanges(log.oldValues, log.newValues)}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredLogs.length === 0 && (
        <div className="text-center py-12 text-hdlcc-textMuted">
          <p>No audit log entries found.</p>
        </div>
      )}
    </div>
  );
}

export default AuditLog;
